import type { PillProps } from "@validereinc/common-components";
import {
  ArtifactRefSchema,
  type ArtifactRefType,
  ApprovalDecisionMetadataSchema,
  type ApprovalDecisionType,
  InvestigationMessageKind,
  InvestigationMessageRole,
  InvestigationStatus,
  TERMINAL_INVESTIGATION_STATUSES,
  type InvestigationMessageType,
  type InvestigationStatusType,
  type InvestigationType,
} from "@validereinc/domain";
import { z } from "zod";

/** One page from `useInfiniteQuery` for investigation messages (newest page first). */
export type InvestigationMessagesPageType = {
  data: InvestigationMessageType[];
};

/**
 * Pages come back newest-first; flatten then reverse so the feed renders oldest-first
 * (chat-style).
 */
export const flattenMessagesOldestFirst = (
  pages: InvestigationMessagesPageType[] | undefined
): InvestigationMessageType[] => {
  if (!pages?.length) return [];
  const flat = pages.flatMap((page) => page.data);
  return flat.slice().reverse();
};

export type PendingApprovalResolutionType = {
  decisionMessageId: string;
  decision: ApprovalDecisionType | null;
};

export type PendingApprovalPairingType = {
  openPendingApprovalMessage: InvestigationMessageType | null;
  resolutionsByPendingApprovalId: Map<string, PendingApprovalResolutionType>;
};

export const pairPendingApprovals = (
  messages: InvestigationMessageType[]
): PendingApprovalPairingType => {
  let openPendingApprovalMessage: InvestigationMessageType | null = null;
  let lastUnpairedPendingApprovalId: string | null = null;
  const resolutionsByPendingApprovalId = new Map<
    string,
    PendingApprovalResolutionType
  >();

  for (const message of messages) {
    if (message.kind === InvestigationMessageKind.PENDING_APPROVAL) {
      openPendingApprovalMessage = message;
      lastUnpairedPendingApprovalId = message.id;
      continue;
    }

    if (
      message.kind === InvestigationMessageKind.APPROVAL_DECISION &&
      lastUnpairedPendingApprovalId
    ) {
      const parsedDecisionMetadata = ApprovalDecisionMetadataSchema.safeParse(
        message.metadata
      );
      resolutionsByPendingApprovalId.set(lastUnpairedPendingApprovalId, {
        decisionMessageId: message.id,
        decision: parsedDecisionMetadata.success
          ? parsedDecisionMetadata.data.decision
          : null,
      });
      openPendingApprovalMessage = null;
      lastUnpairedPendingApprovalId = null;
    }
  }

  return { openPendingApprovalMessage, resolutionsByPendingApprovalId };
};

const InvestigationMessageArtifactsMetadataSchema = z.object({
  artifacts: z.array(ArtifactRefSchema),
});

/**
 * Safely parse typed message artifacts from opaque `message.metadata`.
 * Unknown or malformed payloads are treated as "no artifacts" so the chat feed
 * remains resilient to historical rows that predate this schema.
 */
export const parseMessageArtifacts = (
  metadata: InvestigationMessageType["metadata"]
): ArtifactRefType[] => {
  const parsedMetadata =
    InvestigationMessageArtifactsMetadataSchema.safeParse(metadata);
  if (!parsedMetadata.success) return [];
  return parsedMetadata.data.artifacts;
};

export type ConversationRenderableUnitType =
  | {
      type: "passthrough";
      message: InvestigationMessageType;
    }
  | {
      type: "agent";
      message: InvestigationMessageType;
      artifacts: ArtifactRefType[];
      messageIds: string[];
    };

/**
 * Groups adjacent agent text/artifact rows into one render unit so artifact previews
 * are rendered under the corresponding agent message.
 */
export const groupConversationRenderableUnits = (
  messages: InvestigationMessageType[]
): ConversationRenderableUnitType[] => {
  const visibleMessages = messages.filter(
    (message) =>
      !(
        message.kind === InvestigationMessageKind.APPROVAL_DECISION &&
        !message.content?.trim()
      )
  );
  const renderableUnits: ConversationRenderableUnitType[] = [];
  let openAgentUnit:
    | {
        message: InvestigationMessageType;
        artifacts: ArtifactRefType[];
        messageIds: string[];
      }
    | undefined;

  const flushOpenAgentUnit = () => {
    if (!openAgentUnit) return;
    renderableUnits.push({
      type: "agent",
      message: openAgentUnit.message,
      artifacts: openAgentUnit.artifacts,
      messageIds: openAgentUnit.messageIds,
    });
    openAgentUnit = undefined;
  };

  for (const message of visibleMessages) {
    if (
      message.role === InvestigationMessageRole.AGENT &&
      message.kind === InvestigationMessageKind.PENDING_APPROVAL
    ) {
      flushOpenAgentUnit();
      renderableUnits.push({
        type: "passthrough",
        message,
      });
      continue;
    }

    if (
      message.role === InvestigationMessageRole.AGENT &&
      message.kind === InvestigationMessageKind.MESSAGE
    ) {
      flushOpenAgentUnit();
      openAgentUnit = {
        message,
        artifacts: parseMessageArtifacts(message.metadata),
        messageIds: [message.id],
      };
      continue;
    }

    if (
      message.role === InvestigationMessageRole.AGENT &&
      message.kind === InvestigationMessageKind.ARTIFACT
    ) {
      const artifacts = parseMessageArtifacts(message.metadata);
      if (openAgentUnit) {
        openAgentUnit.artifacts.push(...artifacts);
        openAgentUnit.messageIds.push(message.id);
        continue;
      }

      renderableUnits.push({
        type: "agent",
        message,
        artifacts,
        messageIds: [message.id],
      });
      continue;
    }

    flushOpenAgentUnit();
    renderableUnits.push({
      type: "passthrough",
      message,
    });
  }

  flushOpenAgentUnit();
  return renderableUnits;
};

/** Display values for `error.recoverable` on the investigation detail error block. */
export const InvestigationRecoverable = {
  YES: "yes",
  NO: "no",
  UNKNOWN: "unknown",
} as const;
export type InvestigationRecoverableType =
  (typeof InvestigationRecoverable)[keyof typeof InvestigationRecoverable];

export const formatInvestigationRecoverable = (
  recoverable: unknown
): InvestigationRecoverableType => {
  if (recoverable === true) return InvestigationRecoverable.YES;
  if (recoverable === false) return InvestigationRecoverable.NO;
  return InvestigationRecoverable.UNKNOWN;
};

export const InvestigationStatusToPillVariantMapping: Record<
  InvestigationStatusType,
  PillProps["variant"]
> = {
  [InvestigationStatus.PENDING]: "default",
  [InvestigationStatus.RUNNING]: "info",
  [InvestigationStatus.AWAITING_EXECUTION]: "info",
  [InvestigationStatus.DONE]: "success",
  [InvestigationStatus.ERROR]: "error",
};

const AGENT_TURN_IN_PROGRESS_STATUSES: ReadonlySet<InvestigationStatusType> =
  new Set([InvestigationStatus.PENDING, InvestigationStatus.RUNNING]);

const IN_PROGRESS_INVESTIGATION_STATUSES: ReadonlySet<InvestigationStatusType> =
  new Set([
    InvestigationStatus.PENDING,
    InvestigationStatus.RUNNING,
    InvestigationStatus.AWAITING_EXECUTION,
  ]);

const TERMINAL_INVESTIGATION_STATUS_SET: ReadonlySet<InvestigationStatusType> =
  new Set(TERMINAL_INVESTIGATION_STATUSES);

/**
 * Whether the investigation has finished successfully or failed. Used when
 * stopping polls and when deciding whether to run a one-shot messages refetch
 * after a busy → terminal transition.
 */
export const isTerminalInvestigationStatus = (
  status: InvestigationStatusType
): boolean => TERMINAL_INVESTIGATION_STATUS_SET.has(status);

/**
 * Whether the investigation is in flight for input and refetch purposes
 * (agent turn or post-approval tool execution). Drives composer disable and
 * `useInvestigation` busy → terminal messages invalidation.
 */
export const isAgentBusy = (status?: InvestigationStatusType): boolean =>
  status !== undefined && IN_PROGRESS_INVESTIGATION_STATUSES.has(status);

/** Agent turn only — drives the step-based `ProgressTicker` (`agent_turn`). */
export const isAgentTurnInProgress = (
  status?: InvestigationStatusType
): boolean =>
  status !== undefined && AGENT_TURN_IN_PROGRESS_STATUSES.has(status);

/** Approved tool still executing (investigation `awaiting_execution`). */
export const isToolInProgress = (status?: InvestigationStatusType): boolean =>
  status === InvestigationStatus.AWAITING_EXECUTION;

/**
 * User-facing label for an investigation row. Uses the persisted title when
 * present; otherwise falls back to a truncated id (`Investigation 4c374df8`).
 */
export const getInvestigationDisplayTitle = (
  investigation: Pick<InvestigationType, "id" | "title">
): string => {
  const title = investigation.title?.trim();

  if (title) {
    return title;
  }

  return `Investigation ${investigation.id.slice(0, 8)}`;
};
